import { ArrowLeftIcon, CheckCircleIcon, Loader2Icon, MailIcon, ShieldIcon } from 'lucide-react'
import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'

import api from '@nebula/shared/services/api'
import { usePortalStore } from '@nebula/shared/store'
import { reportAuthRecoveryFailure } from '@nebula/shared/utils/authTelemetry'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const { theme, isPortalDomain } = usePortalStore()

  const primaryColor = isPortalDomain && theme ? theme.primaryColor : '#9333ea'
  const portalName = isPortalDomain && theme ? theme.portalName : 'Nebvla Cloud'
  const passwordResetEnabled =
    !isPortalDomain || !theme || theme.featuresEnabled.passwordReset !== false

  const backgroundStyle =
    isPortalDomain && theme
      ? theme.backgroundImageUrl
        ? {
            backgroundImage: `url(${theme.backgroundImageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }
        : {
            backgroundColor: '#030712',
            backgroundImage: `radial-gradient(ellipse at 50% 40%, ${theme.secondaryColor}25 0%, transparent 60%)`,
          }
      : undefined

  if (!passwordResetEnabled) {
    return <Navigate to="/login" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await api.post('/api/v1/auth/forgot-password', {
        email,
        frontend_url: window.location.origin,
      })
      setSuccess(true)
    } catch (error) {
      // A tela continua neutra (não revelar se o e-mail existe) — mas o erro PARA
      // de ser descartado. Um 403 de escopo, um 500 ou a rede fora eram
      // indistinguíveis de "enviamos o link", e foi assim que a #786 ficou meses
      // sem sinal nenhum de que nada saía.
      // `setSuccess` PRIMEIRO: se a telemetria lançasse, a tela ficaria sem sucesso
      // e sem erro (o `finally` só desliga o loading). O backend blinda esse mesmo
      // caminho com `except`; aqui basta a ordem.
      setSuccess(true)
      reportAuthRecoveryFailure('forgot-password', error)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div
        className={`flex min-h-screen items-center justify-center ${isPortalDomain && theme ? '' : 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900'}`}
        style={backgroundStyle}
      >
        <div className="w-full max-w-md p-4">
          <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-lg">
            <div className="mb-6 flex justify-center">
              <div className="rounded-full bg-green-600/20 p-4">
                <CheckCircleIcon className="size-12 text-green-500" />
              </div>
            </div>

            <h1 className="mb-3 text-center text-2xl font-bold text-white">Email enviado</h1>
            <p className="mb-8 text-center text-gray-300">
              Se o email <strong className="text-white">{email}</strong> estiver cadastrado, você
              receberá um link para redefinir sua senha.
            </p>

            <Link
              to="/login"
              className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: primaryColor }}
            >
              <ArrowLeftIcon className="size-4" />
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`flex min-h-screen items-center justify-center ${isPortalDomain && theme ? '' : 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900'}`}
      style={backgroundStyle}
    >
      <div className="w-full max-w-md p-4">
        <div className="rounded-2xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-lg">
          <div className="mb-6 flex justify-center">
            {isPortalDomain && theme?.logoUrl ? (
              <img
                src={theme.logoUrl}
                alt={portalName}
                className="max-h-24 w-auto max-w-[280px] object-contain"
              />
            ) : (
              <div className="rounded-full p-4" style={{ backgroundColor: `${primaryColor}33` }}>
                <ShieldIcon className="size-12" style={{ color: primaryColor }} />
              </div>
            )}
          </div>

          <h1 className="mb-2 text-center text-2xl font-bold text-white">Recuperar senha</h1>
          <p className="mb-8 text-center text-gray-300">
            Digite seu email para receber um link de recuperação.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-200">Email</label>
              <div className="relative">
                <MailIcon className="absolute top-1/2 left-4 size-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full rounded-lg border border-white/20 bg-white/10 py-3 pr-4 pl-12 text-white placeholder-gray-400 focus:ring-2 focus:outline-none"
                  style={{ '--tw-ring-color': primaryColor } as React.CSSProperties}
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email}
              className="flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? (
                <>
                  <Loader2Icon className="size-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar link de recuperação'
              )}
            </button>
          </form>

          <div className="mt-6">
            <Link
              to="/login"
              className="flex w-full items-center justify-center gap-2 py-3 text-gray-300 transition-colors hover:text-white"
            >
              <ArrowLeftIcon className="size-4" />
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage
